import React, { useCallback, useEffect, useState } from 'react';
import AdminShell, { useAdminKey } from '../components/admin/AdminShell';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const MigrationsBody = () => {
  const { adminKey } = useAdminKey();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/admin/migrations/status?admin_key=${encodeURIComponent(adminKey)}`
      );
      if (!res.ok) {
        setError(`Failed to load (HTTP ${res.status}).`);
        setLoading(false);
        return;
      }
      setData(await res.json());
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, [API_URL, adminKey]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const steps = data?.steps || [];
  const orphaned = data?.orphaned || [];
  const shown = steps.filter((s) => {
    if (filter === 'applied') return s.applied;
    if (filter === 'pending') return !s.applied;
    return true;
  });

  return (
    <div className="max-w-5xl" data-testid="admin-migrations-page">
      <div className="flex items-center justify-end mb-4">
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="bg-[#2C2823] hover:bg-[#1A1816] disabled:opacity-60 text-white px-4 py-1.5 rounded-full text-xs font-semibold"
          data-testid="admin-migrations-refresh-btn"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4"
          data-testid="admin-migrations-error"
        >
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-xl border border-[#E8DED3] p-4" data-testid="stat-db">
              <div className="text-xs uppercase text-[#9B8F88]">Database</div>
              <div className="text-lg font-semibold break-all text-[#2C2823]">{data.db_name}</div>
            </div>
            <div className="bg-white rounded-xl border border-[#E8DED3] p-4" data-testid="stat-registered">
              <div className="text-xs uppercase text-[#9B8F88]">Registered</div>
              <div className="text-2xl font-bold text-[#2C2823]">{data.total_registered}</div>
            </div>
            <div className="bg-white rounded-xl border border-[#E8DED3] p-4" data-testid="stat-applied">
              <div className="text-xs uppercase text-[#9B8F88]">Applied</div>
              <div className="text-2xl font-bold text-emerald-700">{data.total_applied}</div>
            </div>
            <div className="bg-white rounded-xl border border-[#E8DED3] p-4" data-testid="stat-pending">
              <div className="text-xs uppercase text-[#9B8F88]">Pending</div>
              <div className={`text-2xl font-bold ${data.pending.length ? 'text-amber-700' : 'text-[#9B8F88]'}`}>
                {data.pending.length}
              </div>
            </div>
          </div>

          <div className="mb-3 flex gap-2">
            {['all', 'applied', 'pending'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-sm border ${
                  filter === f
                    ? 'bg-[#E85D35] text-white border-[#E85D35]'
                    : 'bg-white text-[#2C2823] border-[#E8DED3] hover:bg-[#FFF5EB]'
                }`}
                data-testid={`admin-migrations-filter-${f}`}
              >
                {f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-[#E8DED3] overflow-hidden" data-testid="admin-migrations-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FFF5EB] text-left text-[#2C2823]">
                <tr>
                  <th className="px-4 py-2 w-10">#</th>
                  <th className="px-4 py-2">Migration ID</th>
                  <th className="px-4 py-2">Applied At</th>
                  <th className="px-4 py-2 w-24">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s, i) => (
                  <tr key={s.migration_id} className="border-t border-[#E8DED3]" data-testid={`row-${s.migration_id}`}>
                    <td className="px-4 py-2 text-[#9B8F88]">{i + 1}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#2C2823]">{s.migration_id}</td>
                    <td className="px-4 py-2 text-[#6B6560]">{formatDate(s.applied_at)}</td>
                    <td className="px-4 py-2">
                      {s.applied ? (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold">
                          applied
                        </span>
                      ) : (
                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">
                          pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-4 py-6 text-center text-[#9B8F88]">
                      Nothing to show for this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {orphaned.length > 0 && (
            <div className="mt-6" data-testid="admin-migrations-orphaned">
              <h2 className="text-lg font-semibold mb-2 text-red-700">
                Orphaned ({orphaned.length})
              </h2>
              <p className="text-xs text-[#6B6560] mb-2">
                These migration IDs are marked applied in the DB but no longer exist in <code>STEPS</code>.
              </p>
              <div className="bg-white rounded-xl border border-[#E8DED3] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 text-left">
                    <tr>
                      <th className="px-4 py-2">Migration ID</th>
                      <th className="px-4 py-2">Applied At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orphaned.map((o) => (
                      <tr key={o.migration_id} className="border-t border-[#E8DED3]">
                        <td className="px-4 py-2 font-mono text-xs">{o.migration_id}</td>
                        <td className="px-4 py-2 text-[#6B6560]">{formatDate(o.applied_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AdminMigrationsPage = () => (
  <AdminShell
    title="Migrations"
    subtitle="Registered vs applied DB migration steps"
  >
    <MigrationsBody />
  </AdminShell>
);

export default AdminMigrationsPage;
